import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { ClaimsService } from "../../../services/claims.service";
import { TableModule } from "primeng/table";
import { CommonModule, DatePipe } from "@angular/common";
import { NzToolTipModule } from "ng-zorro-antd/tooltip";
import { Subscription } from "rxjs";
import { LookupService } from "../../../services/lookup.service";
import { SectionsService } from "../../../services/sections.service";


@Component({
    selector: 'ppl-audit-history',
    templateUrl: './audit-history.component.html',
    styleUrls: ['./audit-history.component.css'],
    standalone: true,
    imports: [
        TableModule,
        NzToolTipModule,
        DatePipe,
        CommonModule
    ],
})
export class AuditHistoryComponent implements OnInit, OnDestroy {
    @Input() regionalCenters: any[] = [];
    @Input() serviceCodes: any[] = [];
    @Input() auditEntryId: any;
    @Input() flow: string = '';
    @Input() programCode = '';
    @Input() lineDetailId?: any;
    authHistoryData: any[] = [];
    authHistoryFormattedData: any[] = [];

    private getAuthHistorySub!: Subscription;
    constructor(
        private claimsService: ClaimsService,
        private datePipe: DatePipe,
        private lookupService: LookupService,
        private sectionService: SectionsService,
    ) { }

    ngOnInit(): void {
        if (this.auditEntryId)
            this.getAuthHistory();
    }

    getAuthHistory(): void {
        this.getAuthHistorySub = this.claimsService.getAuditHistoryData(this.programCode, this.auditEntryId, this.flow).subscribe((data: any) => {
            if (data.status == 200) {
                this.authHistoryData = data.responsedata;
                if (this.flow === 'purchaserequest') {
                    this.setPurchaseRequestAuditHistoryTimeLineData();
                } else {
                    this.setAuthHistoryTimeLineData();
                }
            }
        });
    }

    setAuthHistoryTimeLineData(): void {
        this.authHistoryFormattedData = [];
        let columns = this.sectionService.getAuditHistoryColumns(this.flow, this.programCode);
        this.authHistoryData?.forEach((history: any) => {
            const timeLineObj: any = {
                createdDateWithTime: history.createdDate,
                createdDate: this.datePipe.transform(history.createdDate, 'MM/dd/yyyy') ?? '',
                createdBy: history.createdBy,
                modifiedDate: this.datePipe.transform(history.modifiedDate, 'MM/dd/yyyy') ?? '',
                modifiedBy: history.modifiedBy,
                modified_fields: history.modified_fields.filter((mf: any) => columns.some((ah: any) => ah.field === mf.col_name && ah.header != '')).map((field: any) => {
                    if (field.col_name === 'source_org_id' && this.regionalCenters?.length > 0) {
                        const rcName = this.regionalCenters.find(rc => rc.orgId.toString() === field.curr_value)?.orgName;
                        const prevRcName = this.regionalCenters.find(rc => rc.orgId.toString() === field.prev_value)?.orgName;
                        return {
                            curr_value: rcName ?? field.curr_value,
                            prev_value: prevRcName ?? field.prev_value,
                            col_name: columns.find((col: any) => col.field === field.col_name)?.header || field.col_name
                        }
                    }

                    if (field.col_name === 'servicecode_detail_id' && this.serviceCodes?.length > 0) {
                        const scDesc = this.serviceCodes.find(sc => sc.servicecodeDetailId.toString() === field.curr_value)?.description;
                        const prevScDesc = this.serviceCodes.find(rc => rc.servicecodeDetailId.toString() === field.prev_value)?.description;
                        return {
                            curr_value: scDesc ?? field.curr_value,
                            prev_value: prevScDesc ?? field.prev_value,
                            col_name: columns.find((col: any) => col.field === field.col_name)?.header || field.col_name
                        }
                    }
                    if (field.col_name === 'prcocessing_status_lkp') {
                        const processingStatusList = this.lookupService.getLookupValue('TS_App_S');
                        return {
                            curr_value: processingStatusList.find((b: any) => b.lkpValueCode == field.curr_value)?.lkpValueName || field.curr_value,
                            prev_value: processingStatusList.find((b: any) => b.lkpValueCode == field.prev_value)?.lkpValueName || field.prev_value,
                            col_name: columns.find((col: any) => col.field === field.col_name)?.header || field.col_name
                        }
                    }
                    if (field.col_name === 'business_status_lkp') {
                        const businessStatusList = this.lookupService.getLookupValue('Business Status');
                        return {
                            curr_value: businessStatusList.find((b: any) => b.lkpValueCode == field.curr_value)?.lkpValueName || field.curr_value,
                            prev_value: businessStatusList.find((b: any) => b.lkpValueCode == field.prev_value)?.lkpValueName || field.prev_value,
                            col_name: columns.find((col: any) => col.field === field.col_name)?.header || field.col_name
                        }
                    }

                    return {
                        curr_value: field.curr_value,
                        prev_value: field.prev_value,
                        col_name: columns.find((col: any) => col.field === field.col_name)?.header || field.col_name
                    }
                })
            }
            this.authHistoryFormattedData.push(timeLineObj);
        })
    }

    setPurchaseRequestAuditHistoryTimeLineData(): void {
        this.authHistoryFormattedData = [];
        let columns = this.sectionService.getAuditHistoryColumns(this.flow, this.programCode);

        const filteredData = this.authHistoryData?.filter((history: any) => {
            const lineDetailId = history.modified_data?.[0]?.tas_purchaserequest_line_detail_id;
            if (lineDetailId === null || lineDetailId === undefined) {
                return true;
            }

            return lineDetailId === this.lineDetailId;
        });

        filteredData?.forEach((history: any) => {
            const timeLineObj: any = {
                createdDateWithTime: history.createdDate,
                createdDate: this.datePipe.transform(history.createdDate, 'MM/dd/yyyy') ?? '',
                createdBy: history.createdBy,
                modifiedDate: this.datePipe.transform(history.modifiedDate, 'MM/dd/yyyy') ?? '',
                modifiedBy: history.modifiedBy,
                modified_fields: history.modified_fields.filter((mf: any) => columns.some((ah: any) => ah.field === mf.col_name && ah.header != '')).map((field: any) => {
                    if (field.col_name === 'source_org_id' && this.regionalCenters?.length > 0) {
                        const rcName = this.regionalCenters.find(rc => rc.orgId.toString() === field.curr_value)?.orgName;
                        const prevRcName = this.regionalCenters.find(rc => rc.orgId.toString() === field.prev_value)?.orgName;
                        return {
                            curr_value: rcName ?? field.curr_value,
                            prev_value: prevRcName ?? field.prev_value,
                            col_name: columns.find((col: any) => col.field === field.col_name)?.header || field.col_name
                        }
                    }

                    if (field.col_name === 'servicecode_detail_id' && this.serviceCodes?.length > 0) {
                        const scDesc = this.serviceCodes.find(sc => sc.servicecodeDetailId.toString() === field.curr_value)?.description;
                        const prevScDesc = this.serviceCodes.find(rc => rc.servicecodeDetailId.toString() === field.prev_value)?.description;
                        return {
                            curr_value: scDesc ?? field.curr_value,
                            prev_value: prevScDesc ?? field.prev_value,
                            col_name: columns.find((col: any) => col.field === field.col_name)?.header || field.col_name
                        }
                    }
                    if (field.col_name === 'prcocessing_status_lkp') {
                        const processingStatusList = this.lookupService.getLookupValue('TS_App_S');
                        return {
                            curr_value: processingStatusList.find((b: any) => b.lkpValueCode == field.curr_value)?.lkpValueName || field.curr_value,
                            prev_value: processingStatusList.find((b: any) => b.lkpValueCode == field.prev_value)?.lkpValueName || field.prev_value,
                            col_name: columns.find((col: any) => col.field === field.col_name)?.header || field.col_name
                        }
                    }
                    if (field.col_name === 'business_status_lkp') {
                        const businessStatusList = this.lookupService.getLookupValue('Business Status');
                        return {
                            curr_value: businessStatusList.find((b: any) => b.lkpValueCode == field.curr_value)?.lkpValueName || field.curr_value,
                            prev_value: businessStatusList.find((b: any) => b.lkpValueCode == field.prev_value)?.lkpValueName || field.prev_value,
                            col_name: columns.find((col: any) => col.field === field.col_name)?.header || field.col_name
                        }
                    }

                    return {
                        curr_value: field.curr_value,
                        prev_value: field.prev_value,
                        col_name: columns.find((col: any) => col.field === field.col_name)?.header || field.col_name
                    }
                })
            }

            if (timeLineObj.modified_fields.length > 0) {
                this.authHistoryFormattedData.push(timeLineObj);
            }
        });
    }

    ngOnDestroy(): void {
        if (this.getAuthHistorySub) {
            this.getAuthHistorySub.unsubscribe();
        }
    }
}
